import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ArchetypeId } from '@llura/types';

import { Button } from '@llura/ui';

import { useStrings } from '../../hooks/useStrings';
import { useProgressStore } from '../../stores/progress.store';
import { ARCHETYPES, getArchetype } from '../../lib/archetypes';

function pad(n: number) {
  return String(n).padStart(2, '0');
}
function isoFor(year: number, monthIdx: number, day: number): string {
  return `${year}-${pad(monthIdx + 1)}-${pad(day)}`;
}
function todayISO(): string {
  const d = new Date();
  return isoFor(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Calendar grid of the last 90 days' archetype selections. Each cell shows
 * the archetype glyph painted with that archetype's primary color, or an
 * empty muted square. Tapping a cell reveals its archetype name + tagline
 * inline.
 */
export function MoodHistoryPage() {
  const navigate = useNavigate();
  const { strings } = useStrings();
  const selectedByDay = useProgressStore((s) => s.selectedArchetypeByDay);

  const [focusMonth, setFocusMonth] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });

  const grid = useMemo(() => {
    const first = new Date(focusMonth.year, focusMonth.month, 1);
    const daysInMonth = new Date(focusMonth.year, focusMonth.month + 1, 0).getDate();
    // Monday-first weekday index (0=Mon, 6=Sun).
    const leading = (first.getDay() + 6) % 7;
    const cells: ({ day: number; iso: string } | null)[] = [];
    for (let i = 0; i < leading; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({ day: d, iso: isoFor(focusMonth.year, focusMonth.month, d) });
    }
    return cells;
  }, [focusMonth]);

  const [revealed, setRevealed] = useState<string | null>(null);

  const monthLabel = useMemo(
    () =>
      new Date(focusMonth.year, focusMonth.month, 1).toLocaleString(undefined, {
        month: 'long',
        year: 'numeric',
      }),
    [focusMonth],
  );

  const today = todayISO();
  const totalRecorded = Object.keys(selectedByDay).length;

  const goPrev = () =>
    setFocusMonth((s) => {
      const m = s.month - 1;
      return m < 0 ? { year: s.year - 1, month: 11 } : { year: s.year, month: m };
    });
  const goNext = () =>
    setFocusMonth((s) => {
      const m = s.month + 1;
      return m > 11 ? { year: s.year + 1, month: 0 } : { year: s.year, month: m };
    });

  const revealedArchetypeId = revealed ? selectedByDay[revealed] : null;

  return (
    <div className="mx-auto w-full max-w-md space-y-6 px-4 py-6">
      <header className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/dashboard')}
          className="rounded-full"
          aria-label={strings.moodHistory.backAria}
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div>
          <p className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground">
            {strings.moodHistory.eyebrow}
          </p>
          <h1 className="font-serif text-2xl">{strings.moodHistory.title}</h1>
        </div>
      </header>

      {totalRecorded === 0 ? (
        <div className="space-y-4 rounded-2xl border border-border bg-card p-5 text-center">
          <p className="text-sm text-muted-foreground">{strings.moodHistory.emptyState}</p>
          <Button onClick={() => navigate('/dashboard')} className="mx-auto">
            {strings.moodHistory.emptyCta}
          </Button>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="icon"
              onClick={goPrev}
              className="rounded-full"
              aria-label={strings.moodHistory.prevMonthAria}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <p className="font-serif text-lg">{monthLabel}</p>
            <Button
              variant="ghost"
              size="icon"
              onClick={goNext}
              className="rounded-full"
              aria-label={strings.moodHistory.nextMonthAria}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="grid grid-cols-7 gap-2">
            {grid.map((cell, i) => {
              if (!cell) return <div key={`gap-${i}`} className="aspect-square" />;
              const id = selectedByDay[cell.iso] as ArchetypeId | undefined;
              const isToday = cell.iso === today;
              if (!id) {
                return (
                  <div
                    key={cell.iso}
                    className={`aspect-square rounded-lg border border-border/40 bg-muted/20 text-[10px] flex items-center justify-center text-muted-foreground ${
                      isToday ? 'ring-1 ring-primary/60' : ''
                    }`}
                  >
                    {cell.day}
                  </div>
                );
              }
              const a = getArchetype(id);
              const active = revealed === cell.iso;
              return (
                <button
                  type="button"
                  key={cell.iso}
                  onClick={() => setRevealed((r) => (r === cell.iso ? null : cell.iso))}
                  aria-pressed={active}
                  aria-label={`${strings.archetypes[id].title} · ${cell.iso}`}
                  className={`relative aspect-square rounded-lg border text-[10px] flex items-center justify-center transition-transform hover:scale-[1.04] ${
                    active ? 'scale-[1.04]' : ''
                  } ${isToday ? 'ring-1 ring-primary/60' : ''}`}
                  style={{
                    color: `hsl(${a.color.primary})`,
                    backgroundColor: `hsl(${a.color.primary} / 0.18)`,
                    borderColor: `hsl(${a.color.primary} / 0.45)`,
                  }}
                >
                  <a.Icon width={20} height={20} />
                  <span className="absolute right-1 top-1 text-[9px] text-muted-foreground">
                    {cell.day}
                  </span>
                </button>
              );
            })}
          </div>

          {revealed && revealedArchetypeId && (
            <div className="rounded-2xl border border-border bg-card p-4 text-sm">
              <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                {revealed}
              </p>
              <p className="mt-1 font-serif text-lg">
                {strings.archetypes[revealedArchetypeId].title}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {strings.archetypes[revealedArchetypeId].tagline}
              </p>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-center gap-3 pt-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            {ARCHETYPES.map((a) => (
              <span key={a.id} className="inline-flex items-center gap-1">
                <span
                  aria-hidden
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ backgroundColor: `hsl(${a.color.primary})` }}
                />
                {strings.archetypes[a.id].title.replace(/^The /, '')}
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
